import { Button, Card, Dropdown, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import SubtleBadge from '@/components/common/SubtleBadge';
import downloadxls from '@/helpers/downloadxls';
import {
  buildTableExcelRows,
  copyKeywords,
  exportDecodedJson,
  exportPathsCsv,
  getExportBaseName
} from '../utils/exportCluster';
import { CLUSTER_MODES } from '../utils/recluster';
import { successMessage } from '@/utils/toast';
import Icon from './Icon';
import VolumeThresholdLegend from './VolumeThresholdLegend';
import type {
  ClusterData,
  ClusterModeId,
  ClusterTableRow,
  VolumeThresholds
} from '../types';

type ClusterExportToolbarProps = {
  rows?: ClusterTableRow[];
  clusterData?: ClusterData | null;
  clusterMode?: ClusterModeId | string;
  onClusterModeChange?: (mode: ClusterModeId | string) => void;
  pathCount?: number;
  clusterSummary?: string | null;
  thresholds?: VolumeThresholds | null;
  /** When set, shows a toggle to expand/collapse all tree rows. */
  allExpanded?: boolean;
  onToggleExpandAll?: () => void;
  depthFilter?: number | null;
  onDepthFilterChange?: (value: number) => void;
  maxDepth?: number;
};

const treeBtnClass =
  'bg-100 d-inline-flex align-items-center justify-content-center p-2 text-700 border border-400 rounded-3 lh-1';

export default function ClusterExportToolbar({
  rows = [],
  clusterData,
  clusterMode = 'default',
  onClusterModeChange,
  pathCount = 0,
  clusterSummary = '',
  thresholds = null,
  allExpanded = true,
  onToggleExpandAll,
  depthFilter = null,
  onDepthFilterChange,
  maxDepth = 0
}: ClusterExportToolbarProps) {
  const showFooter = Boolean(thresholds) || Boolean(clusterSummary);
  const expandToggleTitle = allExpanded
    ? 'بستن همه نودها'
    : 'باز کردن همه نودها';
  const showDepthFilter = typeof onDepthFilterChange === 'function';
  const depthOptions = Array.from(
    { length: Math.max(0, maxDepth) },
    (_, i) => i + 1
  );

  return (
    <Card className="mb-0">
      <Card.Body className="py-3 px-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Select
              size="sm"
              className="form-select"
              style={{ width: 200 }}
              value={clusterMode}
              onChange={e => onClusterModeChange?.(e.target.value)}
              aria-label="کلاستر بر اساس"
            >
              {CLUSTER_MODES.map(mode => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </Form.Select>

            {showDepthFilter ? (
              <Form.Select
                size="sm"
                className="form-select"
                style={{ width: 140 }}
                value={depthFilter ?? ''}
                onChange={e => onDepthFilterChange?.(Number(e.target.value))}
                aria-label="فیلتر عمق"
              >
                {depthOptions.map(depth => (
                  <option key={depth} value={depth}>
                    تا عمق {depth}
                  </option>
                ))}
              </Form.Select>
            ) : null}

            <SubtleBadge
              bg="secondary"
              className="d-inline-flex align-items-center"
            >
              <Icon name="list-ul" className="ms-1" />
              {pathCount} مسیر
            </SubtleBadge>
          </div>

          <div className="d-inline-flex flex-nowrap align-items-center gap-1 flex-shrink-0">
            {onToggleExpandAll ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tip-cluster-table-expand-toggle">
                    {expandToggleTitle}
                  </Tooltip>
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="link"
                  className={treeBtnClass}
                  onClick={onToggleExpandAll}
                  aria-label={expandToggleTitle}
                >
                  <Icon
                    name={allExpanded ? 'arrows-collapse' : 'arrows-expand'}
                    className="fs--1"
                  />
                </Button>
              </OverlayTrigger>
            ) : null}

            {clusterData ? (
              <Dropdown align="start">
                <Dropdown.Toggle
                  variant="falcon-default"
                  size="sm"
                  className="d-inline-flex align-items-center justify-content-center text-600"
                  id="cluster-export-dropdown"
                >
                  <Icon name="download" />
                  خروجی
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="shadow border-0 py-1"
                  style={{ minWidth: 190 }}
                  dir="rtl"
                >
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-1 px-2 fs--1"
                    onClick={() => {
                      downloadxls(
                        buildTableExcelRows(clusterData, rows),
                        `${getExportBaseName(clusterData)}-table`
                      );
                      successMessage('Excel دانلود شد');
                    }}
                  >
                    <FaFileExcel size={14} className="text-success" />
                    <span>خروجی Excel</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-1 px-2 fs--1"
                    onClick={() => {
                      exportPathsCsv(clusterData);
                      successMessage('CSV دانلود شد');
                    }}
                  >
                    <Icon name="filetype-csv" className="text-primary fs--2" />
                    <span>خروجی CSV</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-1 px-2 fs--1"
                    onClick={() => {
                      exportDecodedJson(clusterData);
                      successMessage('JSON دانلود شد');
                    }}
                  >
                    <Icon name="filetype-json" className="text-primary fs--2" />
                    <span>خروجی JSON</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2 py-1 px-2 fs--1"
                    onClick={async () => {
                      const count = await copyKeywords(clusterData);
                      successMessage(`${count} کلمه کپی شد`);
                    }}
                  >
                    <Icon name="copy" className="text-primary fs--2" />
                    <span>کپی همه کلمات</span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </div>
        </div>

        {showFooter ? (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top border-200">
            {thresholds ? (
              <VolumeThresholdLegend thresholds={thresholds} />
            ) : (
              <span className="fs--2 text-600">{clusterSummary}</span>
            )}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}
